/**
 * Defense-in-depth input sanitizers for business-listing user input.
 *
 * Goals:
 *   - Prevent stored-XSS by stripping HTML tags + dangerous attrs before save.
 *   - Block javascript:/data: URLs in user-supplied links.
 *   - Make obvious AI prompt-injection text inert (if/when listing data is
 *     ever passed to an LLM downstream).
 *   - Enforce per-field length budgets so a single user can't bloat a doc.
 *
 * These run client-side as a first pass. The cloud function MUST run the
 * matching server-side allow-list (functions/businessManagementFn.js) before
 * any write; never trust the client.
 */

// Strip HTML tags, including stray angle-bracket fragments.
const TAG_RE = /<\/?[a-zA-Z][^>]*>?/g;
// Disallowed control chars (keep \t, \n, \r).
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;

const PROMPT_INJECTION_PATTERNS = [
  /<\|im_(?:start|end)\|>/gi,
  /<\|system\|>/gi,
  /\[\/?(?:INST|SYS)\]/gi,
  /\bignore (?:all )?(?:previous|prior|above) (?:instructions|prompts?)\b/gi,
  /\bdisregard (?:all )?(?:previous|prior|above) (?:instructions|prompts?)\b/gi,
  /\byou are now\b[^.\n]{0,80}\b(?:dan|jailbroken|unrestricted)\b/gi,
  /^\s*system\s*:/gim,
  /^\s*assistant\s*:/gim,
  /^\s*###\s*(?:instructions?|system|prompt)\b/gim,
];

function stripPromptInjection(value) {
  let out = value;
  for (const re of PROMPT_INJECTION_PATTERNS) {
    out = out.replace(re, "");
  }
  return out;
}

/**
 * Single-line text: strips HTML, control chars, prompt injection markers,
 * collapses internal whitespace, and clamps to `maxLen`.
 */
export function sanitizeText(value, { maxLen = 500 } = {}) {
  if (value == null) return "";
  let v = String(value);
  v = v.replace(TAG_RE, "");
  v = v.replace(CONTROL_RE, "");
  v = stripPromptInjection(v);
  v = v.replace(/\s+/g, " ").trim();
  if (v.length > maxLen) v = v.slice(0, maxLen);
  return v;
}

/**
 * Multiline text: same scrubbing as sanitizeText but preserves newlines and
 * collapses only horizontal whitespace.
 */
export function sanitizeMultiline(value, { maxLen = 4000 } = {}) {
  if (value == null) return "";
  let v = String(value);
  v = v.replace(TAG_RE, "");
  v = v.replace(CONTROL_RE, "");
  v = stripPromptInjection(v);
  // Collapse spaces/tabs runs; cap consecutive newlines at 2.
  v = v.replace(/[ \t]+/g, " ");
  v = v.replace(/\n{3,}/g, "\n\n");
  v = v.trim();
  if (v.length > maxLen) v = v.slice(0, maxLen);
  return v;
}

/**
 * URL: only http(s) allowed; everything else (javascript:, data:, file:, ...)
 * returns null. Trims whitespace. Length capped at 2000 chars.
 */
export function sanitizeUrl(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.length > 2000) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Phone: keep digits, spaces, dashes, parens, plus. Strip everything else.
 */
export function sanitizePhone(value) {
  if (value == null) return "";
  return String(value).replace(/[^\d+()\-.\s]/g, "").trim().slice(0, 40);
}

/**
 * Email: minimal scrub — strip whitespace + HTML, clamp length. Real RFC
 * validation happens elsewhere (formValidation.validators.email).
 */
export function sanitizeEmail(value) {
  if (value == null) return "";
  return String(value)
    .replace(TAG_RE, "")
    .replace(/\s+/g, "")
    .slice(0, 254);
}

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const TIME_RE = /^\d{2}:\d{2}$/;

function sanitizeHours(value) {
  if (!value || typeof value !== "object") return null;
  let anyDay = false;
  const out = {};
  for (const day of DAY_KEYS) {
    const src = value[day];
    if (!src || typeof src !== "object") continue;
    const closed = Boolean(src.closed);
    const open = closed || !TIME_RE.test(String(src.open || "")) ? "" : String(src.open);
    const close = closed || !TIME_RE.test(String(src.close || "")) ? "" : String(src.close);
    if (closed || (open && close)) anyDay = true;
    out[day] = { open, close, closed };
  }
  return anyDay ? out : null;
}

function sanitizeStringList(value, { maxItems = 20, perItemLen = 60 } = {}) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  for (const item of value) {
    const clean = sanitizeText(item, { maxLen: perItemLen });
    if (!clean) continue;
    const key = clean.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean);
    if (out.length >= maxItems) break;
  }
  return out;
}

/**
 * Allow-list sanitizer for a listing payload. Drops any field not explicitly
 * known. Returns a brand-new object — does not mutate input.
 */
export function sanitizeListingPayload(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};

  if ("name" in raw) out.name = sanitizeText(raw.name, { maxLen: 120 });
  if ("description" in raw) out.description = sanitizeMultiline(raw.description, { maxLen: 4000 });
  if ("type" in raw) out.type = sanitizeText(raw.type, { maxLen: 40 });
  if ("category" in raw) out.category = sanitizeText(raw.category, { maxLen: 40 });
  if ("island" in raw) out.island = sanitizeText(raw.island, { maxLen: 20 });
  if ("street" in raw) out.street = sanitizeText(raw.street, { maxLen: 200 });
  if ("street2" in raw) out.street2 = sanitizeText(raw.street2, { maxLen: 100 });
  if ("city" in raw) out.city = sanitizeText(raw.city, { maxLen: 80 });
  if ("zip" in raw) out.zip = sanitizeText(raw.zip, { maxLen: 12 });
  if ("address" in raw) out.address = sanitizeText(raw.address, { maxLen: 300 });
  if ("contact" in raw) out.contact = sanitizeEmail(raw.contact);
  if ("phone" in raw) out.phone = sanitizePhone(raw.phone);
  if ("website" in raw) out.website = sanitizeUrl(raw.website);
  if ("bookingUrl" in raw) out.bookingUrl = sanitizeUrl(raw.bookingUrl);
  if ("hours" in raw) out.hours = sanitizeHours(raw.hours);
  if ("tags" in raw) out.tags = sanitizeStringList(raw.tags, { maxItems: 12, perItemLen: 40 });
  if ("eligiblePassTiers" in raw) out.eligiblePassTiers = sanitizeStringList(raw.eligiblePassTiers, { maxItems: 10, perItemLen: 40 });

  if ("latitude" in raw) {
    const n = Number(raw.latitude);
    out.latitude = Number.isFinite(n) && n >= -90 && n <= 90 ? n : null;
  }
  if ("longitude" in raw) {
    const n = Number(raw.longitude);
    out.longitude = Number.isFinite(n) && n >= -180 && n <= 180 ? n : null;
  }
  if ("bookingEnabled" in raw) out.bookingEnabled = Boolean(raw.bookingEnabled);
  if ("earnsDoubloons" in raw) out.earnsDoubloons = Boolean(raw.earnsDoubloons);

  return out;
}
